import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { useMutation } from 'react-query';
import createDecorator from 'final-form-calculate';
import { useLocation, useHistory, Link } from 'react-router-dom';
import { Form as FinalForm, Field } from 'react-final-form';

import { preparePhone } from 'utils';

import FormattedMessage from '@guestyci/localize/FormattedMessage';
import { useGuestyPay } from '@guestyci/guesty-pay-widget';
import createStyles from '@guestyci/foundation/createStyles';
import TextField from '@guestyci/foundation/TextField';
import Checkbox from '@guestyci/foundation/Checkbox';
import { FormProvider } from '@guestyci/foundation/enums';
import Form from '@guestyci/foundation/Form';
import Divider from '@guestyci/foundation/Divider';
import FormField from '@guestyci/foundation/FormField';
import t from '@guestyci/localize/t.macro';

import Breadcrumbs from 'components/Breadcrumbs';
import ErrorDialog from 'components/ErrorDialog';
import CheckOutSummary from 'components/CheckOutSummary';
import GuestInformation from 'components/GuestInformation';
import ReservationErrorDialog from 'components/ErrorDialog/ReservationErrorDialog';

import useSearchValues from 'hooks/useSearchValues';
import useDio from 'hooks/useDio';
import usePaymentTypeInitialValue from 'hooks/usePaymentTypeInitialValue';

import createInstantReservation from 'api/quote/createInstantReservation';

import { DATE_FORMAT } from 'constants/date';
import { WebsiteSettingsContext } from 'context/WebsiteSettingsContext';
import { find } from 'lodash';
import useGetQuote from 'hooks/useGetQuote';
import useGoogleTagManager from 'hooks/useGoogleTagManager';
import useFacebookPixel from 'hooks/useFacebookPixel';
import { GuestyPayInstantForm } from 'components/InstantForm';
import useGetPathToNavigate from 'hooks/useGetPathToNavigate';

import { BANK_TRANSFER_PAYMENT_TYPE, CREDIT_CARD_PAYMENT_TYPE } from 'constants/constants';

const useStyles = createStyles(({ breakpoints: { create } }) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    background: '#fff',
    '& .col-wrapper': {
      '& [class*="Input-root"]': {
        background: '#fff',
      },
    },
  },
  contentWrapper: {
    background: '#FAFAFA',
    width: '100%',
    maxWidth: 1500,
    borderRadius: 20,
    display: 'flex',
    [create('xs')]: {
      flexWrap: 'wrap',
      padding: '10px 10px',
      justifyContent: 'center',
    },
    [create('md')]: {
      padding: '30px 30px 80px',
    },
    [create('lg')]: {
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  title: {
    marginBottom: 30,
    marginTop: 30,
  },
  pci: {
    marginBottom: 30,
  },
  detailsRoot: {
    [create('lg')]: {
      maxWidth: 450,
    },
    [create('xl')]: {
      width: '100%',
      maxWidth: 890,
    },
  },
  agreements: {
    [create('xs')]: {
      width: 250,
    },
    [create('md')]: {
      width: '100%',
    },
  },
  privacyWrapper: {
    marginTop: 30,
    marginBottom: 20,
  },
  guestInfo: {
    marginBottom: '48px',
  },
  discountsWrapper: {},
  formContainer: {
    [create('xs')]: {
      width: '100%',
    },
    [create('xl')]: {
      width: 560,
    },
  },
}));

const useCreateInstant = () => {
  return useMutation(createInstantReservation);
};

const required = (value) => (value ? undefined : 'Required');

const Checkout = ({ property, providerId }) => {
  const paymentTypeInitialValue = usePaymentTypeInitialValue();
  const {
    companyInfo: { name },
  } = useContext(WebsiteSettingsContext);
  const { getPathWithLocale } = useGetPathToNavigate();
  const [reservationTotalAmount, setReservationTotalAmount] = useState(null);
  const submitRef = useRef(null);
  const [isInvalid, setIsInvalid] = useState(true);
  const history = useHistory();
  const [isAmaryllisError, setIsAmaryllisError] = useState(false);
  const [reservationError, setReservationError] = useState(false);
  const successPath = getPathWithLocale('/instant-success');

  const { minOccupancy, pointofsale, checkOutDateLocalized, checkInDateLocalized } = useSearchValues();
  const { dioTrack } = useDio();
  const { submit } = useGuestyPay();
  const {
    root,
    contentWrapper,
    title,
    formRoot,
    detailsRoot,
    guestInfo,
    agreements,
    privacyWrapper,
    discountsWrapper,
    formContainer,
  } = useStyles();

  const { gtag } = useGoogleTagManager();
  const { fbtrack } = useFacebookPixel();

  const {
    state: { quoteId, ratePlanId },
  } = useLocation();

  const { data: quote, isLoading: isGettingQuote } = useGetQuote(quoteId);

  const plans = quote?.rates?.ratePlans;
  const selectedRatePlan = find(plans, {
    ratePlan: { _id: ratePlanId },
  })?.ratePlan;

  const { currency, hostPayout } = selectedRatePlan?.money || {};

  const { mutate: handleCreateInstantReservation, isLoading: isCreatingInstant } = useCreateInstant();

  const handleChangeReservationTotalAmmount = useCallback(
    (totalAmount) => {
      setReservationTotalAmount(totalAmount);
    },
    [setReservationTotalAmount]
  );

  const trackError = ({ error, additionalData }) => {
    dioTrack('booking_create_error', 'side-effect', {
      error,
      type: 'instant',
      provider: 'amaryllis',
      ...additionalData,
      listings: [
        {
          guests: minOccupancy,
          listing_id: property._id,
          listing_nickname: property.nickname,
          item_title: property.title,
          currency: property.prices.currency,
          price: property.prices.basePrice,
        },
      ],
    });
  };

  const trackSuccess = ({ response, dioAdditionalData }) => {
    const { _id } = response;
    dioTrack('booking_create_success', 'side-effect', {
      reservationId: _id,
      type: 'instant',
      provider: 'amaryllis',
      ...dioAdditionalData,
      listings: [
        {
          guests: minOccupancy,
          listing_id: property._id,
          listing_nickname: property.nickname,
          item_title: property.title,
          currency: property.prices.currency,
          price: property.prices.basePrice,
        },
      ],
    });
    gtag('ecommerce', {
      purchase: {
        actionField: {
          reservationId: _id,
          reservationTotalAmount,
          listingId: property._id,
          listingName: property.title,
          currency: property.prices.currency,
          checkInDate: response.checkInDateLocalized,
          checkOutDate: response.checkOutDateLocalized,
          numberOfGuests: response.guestsCount,
          pointOfSale: pointofsale,
        },
      },
    });
    gtag('purchase', {
      reservationId: _id,
      reservationTotalAmount,
      listingId: property._id,
      listingName: property.title,
      currency: property.prices.currency,
      checkInDate: response.checkInDateLocalized,
      checkOutDate: response.checkOutDateLocalized,
      numberOfGuests: response.guestsCount,
      pointOfSale: pointofsale,
    });
    fbtrack('Purchase', {
      listing_id: [property._id],
      content_name: property.title,
      content_type: property.propertyType,
      currency: property.prices.currency,
      reservationTotalAmount,
      num_guests: minOccupancy,
      check_in_date: checkInDateLocalized,
      check_out_date: checkOutDateLocalized,
      point_of_sale: pointofsale,
    });
  };

  const submitInstant = async (values) => {
    const {
      firstName,
      lastName,
      discounts = null,
      email,
      phone,
      message = null,
      paymentType = CREDIT_CARD_PAYMENT_TYPE,
    } = values;

    const createParams = {
      ratePlanId,
      guest: {
        firstName,
        lastName,
        email,
        phone: preparePhone(phone),
      },
      policy: {
        privacy: {
          version: 1,
          dateOfAcceptance: moment().format(DATE_FORMAT),
          isAccepted: true,
        },
        marketing: {
          isAccepted: !!discounts,
        },
      },
      ...(message && {
        notes: {
          guest: message,
        },
      }),
    };
    if (paymentType === BANK_TRANSFER_PAYMENT_TYPE) {
      handleCreateInstantReservation(
        {
          quoteId,
          params: {
            ...createParams,
            paymentType,
          },
        },
        {
          onSuccess: (response) => {
            trackSuccess({
              response,
              dioAdditionalData: { paymentType },
            });
            history.push({
              pathname: `${successPath}`,
              state: {
                paymentType,
              },
            });
          },
          onError: (error) => {
            trackError({
              error,
              additionalData: { paymentType },
            });
            setReservationError(error?.response?.data?.error?.message || true);
          },
        }
      );
      return;
    }
    let tokenizationResult;
    try {
      tokenizationResult = await submit();
    } catch {
      setIsAmaryllisError(true);
      return;
    }
    if (tokenizationResult?._id) {
      handleCreateInstantReservation(
        {
          quoteId,
          params: {
            ...createParams,
            ccToken: tokenizationResult._id,
          },
        },
        {
          onSuccess: (response) => {
            trackSuccess({
              response,
              dioAdditionalData: { paymentType },
            });
            history.push({
              pathname: `${successPath}`,
              state: {
                paymentType,
              },
            });
          },
          onError: (error) => {
            trackError({
              error,
              additionalData: { paymentType },
            });
            setReservationError(error?.response?.data?.error?.message || true);
          },
        }
      );
    }
  };

  const cardHolderNameDecorator = useMemo(
    () =>
      createDecorator({
        field: 'cardHolderNameAsGuest',
        updates: (value, _, allValues) =>
          value
            ? {
                ...allValues,
                cardHolderFirstName: undefined,
                cardHolderLastName: undefined,
              }
            : allValues,
      }),
    []
  );

  useEffect(() => {
    if (!reservationTotalAmount || !property) return;

    gtag('checkout', {
      ecommerce: {
        checkout: {
          actionField: { step: 1 },
          products: [
            {
              listingId: property._id,
              listingName: property.title,
              totalPrice: reservationTotalAmount,
              currency: property.prices.currency,
              checkInDate: checkInDateLocalized || null,
              checkOutDate: checkOutDateLocalized || null,
              numberOfGuests: minOccupancy || null,
              pointOfSale: pointofsale,
            },
          ],
        },
      },
    });
    fbtrack('InitiateCheckout', {
      listing_id: [property._id],
      content_name: property.title,
      content_type: property.propertyType,
      currency: property.prices.currency,
      value: reservationTotalAmount,
      num_guests: minOccupancy,
      check_in_date: checkInDateLocalized,
      check_out_date: checkOutDateLocalized,
      point_of_sale: pointofsale,
    });
  }, [
    reservationTotalAmount,
    property,
    checkInDateLocalized,
    minOccupancy,
    checkOutDateLocalized,
    pointofsale,
    gtag,
    fbtrack,
  ]);

  return (
    <div className={root}>
      <div className={contentWrapper}>
        <div className={detailsRoot}>
          <Breadcrumbs />
          <TextField variant="h1" className={title}>
            {t('Fill in your details')}
          </TextField>
          <FinalForm
            onSubmit={submitInstant}
            decorators={[cardHolderNameDecorator]}
            provider={FormProvider.Final}
            initialValues={{
              paymentType: paymentTypeInitialValue,
            }}
            render={({ handleSubmit, invalid }) => {
              submitRef.current = handleSubmit;
              setIsInvalid(invalid);
              return (
                <Form
                  title="checkout"
                  onSubmit={handleSubmit}
                  provider={FormProvider.Final}
                  fieldInstance={Field}
                  className={formRoot}
                >
                  <div className={guestInfo}>
                    <GuestInformation />
                  </div>
                  {!isGettingQuote && (
                    <div className={formContainer}>
                      <GuestyPayInstantForm providerId={providerId} currency={currency} hostPayout={hostPayout} />
                    </div>
                  )}
                  <Divider />
                  <FormField className={privacyWrapper} name="privacyPolicy" validate={[required]} type="checkbox">
                    <Checkbox>
                      <TextField className={agreements}>
                        <FormattedMessage
                          defaultMessage=" I have read and accept the <link>Privacy Policy</link>"
                          values={{
                            link: (chunks) => <Link to="/privacy-policy">{chunks}</Link>,
                          }}
                        />
                        {' | '}
                        <FormattedMessage
                          defaultMessage="<link>{name}</link>"
                          values={{
                            name,
                            link: (chunks) => (
                              <Link to="/terms">
                                {chunks} 
                                {' '}
                                {t('Terms and Conditions')}
                              </Link>
                            ),
                          }}
                        />
                      </TextField>
                    </Checkbox>
                  </FormField>
                  <FormField className={discountsWrapper} name="discounts" type="checkbox">
                    <Checkbox>
                      <TextField className={agreements}>
                        {t('I am interested in receiving discounts, promotions and news about {name}', { name })}
                      </TextField>
                    </Checkbox>
                  </FormField>
                </Form>
              );
            }}
          />
        </div>
        <CheckOutSummary
          onChangePriceAmount={handleChangeReservationTotalAmmount}
          submit={submitRef}
          isLoading={isCreatingInstant}
          isFormInvalid={isInvalid}
        />
      </div>
      <ErrorDialog
        isOpen={isAmaryllisError}
        message="Please try again or provide a different payment method"
        handleClose={() => setIsAmaryllisError(false)}
      />
      <ReservationErrorDialog
        error={reservationError}
        open={!!reservationError}
        handleClose={() => setReservationError(false)}
      />
    </div>
  );
};

export default Checkout;
